import React, { useState, useEffect } from "react";
import { Row, Col, Form, Button, Table, InputGroup, Spinner } from "react-bootstrap";
import { useNavigate } from "react-router-dom";

import { _post } from "../../services/apiClient";
import { apiUrls } from "../../config/apiUrls.const";
import { showToast } from "../../services/ToastService";
import { useBusiness } from "../../contexts/BusinessContext";

// --- Structural Typings matching your Api Layouts ---
interface SubcategoryNode {
  id: number;
  subcategory_id: number;
  subcategory: {
    id: number;
    name_en: string;
    subcat_code: string;
  };
}

interface CategoryNode {
  id: number;
  service_group: string;
  category: {
    id: number;
    name_en: string;
    cat_code: string;
  };
  subcategories: SubcategoryNode[];
}

interface CategorizationSchema {
  [groupName: string]: CategoryNode[];
}

interface InventoryServiceItem {
  id: number;
  title: string;
  price: string | number;
}

interface CartesianOrderLineItem {
  id: string; // Unique transient runtime identifier
  group: string;
  categoryId: string | number;
  subcategoryId: string | number;
  business_item_id: number | string;
  title: string;
  quantity: number;
  unit_price: number;
  total: number;
  availableCategories: CategoryNode[];
  availableSubcategories: SubcategoryNode[];
  availableItems: InventoryServiceItem[];
  itemsLoading: boolean;
}

export default function CreateOrder(): React.JSX.Element {
  const navigate = useNavigate();
  const { business } = useBusiness();

  // --- Core Lifecycle States ---
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [metaLoading, setMetaLoading] = useState<boolean>(false);
  const [categorizationData, setCategorizationData] = useState<CategorizationSchema>({});
  const [availableGroups, setAvailableGroups] = useState<string[]>([]);

  // --- Form Structural States ---
  const [customerName, setCustomerName] = useState<string>("");
  const [mobileNo, setMobileNo] = useState<string>("");
  const [email, setEmail] = useState<string>("");
  
  const [orderStatus, setOrderStatus] = useState<number>(0); 
  const [paymentStatus, setPaymentStatus] = useState<number>(0); 
  const [paymentMethod, setPaymentMethod] = useState<string>("Cash"); 
  const [locationType, setLocationType] = useState<"Center" | "Home">("Center"); 
  const [deliveryAddress, setDeliveryAddress] = useState<string>("");
  const [notes, setNotes] = useState<string>("");

  const [couponCode] = useState<string>("");
  const [couponDiscount, setCouponDiscount] = useState<number>(0);

  // --- Dynamic Grid Matrix State ---
  const [cartItems, setCartItems] = useState<CartesianOrderLineItem[]>([]);

  // Phase 1: Retrieve Multilayer Schema on Initialization Mount
  useEffect(() => {
    let isMounted = true;
    async function loadCategorizationSchema(): Promise<void> {
      if (!business?.biz_code) return;
      try {
        setMetaLoading(true);
        // Targets "biz-categorization" framework route
        const response = await _post(apiUrls.getBusinessCategorization, {
          biz_code: business.biz_code,
        });

        if (!isMounted) return;
        const extracted = response?.data?.success ? response.data.data : response?.data;
        
        if (extracted && typeof extracted === "object") {
          setCategorizationData(extracted);
          setAvailableGroups(Object.keys(extracted));
        }
      } catch (err) {
        console.error("Failed parsing master configuration structure schemas:", err);
        showToast({ icon: "error", title: "Structure Error", text: "Could not establish categorization parameters." });
      } finally {
        if (isMounted) setMetaLoading(false);
      }
    }
    loadCategorizationSchema();
    return () => { isMounted = false; };
  }, [business?.biz_code]);

  // Dynamic Row Actions
  const handleAddNewRowInstance = (): void => {
    const freshRow: CartesianOrderLineItem = {
      id: crypto.randomUUID ? crypto.randomUUID() : Math.random().toString(36).substring(2, 9),
      group: "",
      categoryId: "",
      subcategoryId: "",
      business_item_id: "",
      title: "",
      quantity: 1,
      unit_price: 0,
      total: 0,
      availableCategories: [],
      availableSubcategories: [],
      availableItems: [],
      itemsLoading: false,
    };
    setCartItems((prev) => [...prev, freshRow]);
  };

  const handleRemoveRowInstance = (targetId: string): void => {
    setCartItems((prev) => prev.filter((item) => item.id !== targetId));
  };

  const handleGroupSelectionChange = (rowIndex: number, selectedGroup: string): void => {
    const categoriesMatched = categorizationData[selectedGroup] || [];
    setCartItems((prev) => {
      const working = [...prev];
      working[rowIndex] = {
        ...working[rowIndex],
        group: selectedGroup,
        categoryId: "",
        subcategoryId: "",
        business_item_id: "",
        title: "",
        unit_price: 0,
        total: 0,
        availableCategories: categoriesMatched,
        availableSubcategories: [],
        availableItems: [],
      };
      return working;
    });
  };

  const handleCategorySelectionChange = (rowIndex: number, catIdStr: string): void => {
    const catId = parseInt(catIdStr, 10);
    const targetRow = cartItems[rowIndex];
    const categoryMatch = targetRow.availableCategories.find((c) => c.category?.id === catId);
    const subcategoriesMatched = categoryMatch ? categoryMatch.subcategories : [];

    setCartItems((prev) => {
      const working = [...prev];
      working[rowIndex] = {
        ...working[rowIndex],
        categoryId: catIdStr,
        subcategoryId: "",
        business_item_id: "",
        title: "",
        unit_price: 0,
        total: 0,
        availableSubcategories: subcategoriesMatched,
        availableItems: [],
      };
      return working;
    });
  };

  const handleSubcategorySelectionChange = async (rowIndex: number, subcatIdStr: string): Promise<void> => {
    const subcatId = parseInt(subcatIdStr, 10);
    
    // Set baseline state variables to block trace errors
    setCartItems((prev) => {
      const working = [...prev];
      working[rowIndex] = {
        ...working[rowIndex],
        subcategoryId: subcatIdStr,
        business_item_id: "",
        title: "",
        unit_price: 0,
        total: 0,
        itemsLoading: true,
        availableItems: [],
      };
      return working;
    });

    try {
      // Query downstream item assets mapping back matching parameters
      const response = await _post(apiUrls.groupedItems, {
        biz_code: business?.biz_code,
        group: cartItems[rowIndex].group,
      });

      const responseContainer = response?.data?.success ? response.data.data : response?.data;
      const completeItemsList = Array.isArray(responseContainer) 
        ? responseContainer 
        : (responseContainer?.data || []);

      // Client side relational reduction filters inventory by explicit subcategory identifiers
      const targetedAssets = completeItemsList.filter(
        (item: any) => item.subcategory_id === subcatId || item.subcategory?.id === subcatId
      );

      setCartItems((prev) => {
        const working = [...prev];
        working[rowIndex] = {
          ...working[rowIndex],
          availableItems: targetedAssets,
          itemsLoading: false,
        };
        return working;
      });
    } catch (err) {
      console.error("Error retrieving target service elements:", err);
      setCartItems((prev) => {
        const working = [...prev];
        working[rowIndex].itemsLoading = false;
        return working;
      });
    }
  };

  const handleItemSelectionChange = (rowIndex: number, itemIdStr: string): void => {
    const itemId = parseInt(itemIdStr, 10);
    const targetRow = cartItems[rowIndex];
    const itemMatch = targetRow.availableItems.find((item) => item.id === itemId);

    if (!itemMatch) return;

    // Guard duplicate entry structures across siblings
    const duplicateExists = cartItems.some(
      (row, idx) => idx !== rowIndex && Number(row.business_item_id) === itemId
    );

    if (duplicateExists) {
      showToast({ icon: "warning", title: "Duplicate Row Found", text: "This specific asset index is already track-mapped." });
      return;
    }

    const calculatedPrice = typeof itemMatch.price === "string" ? parseFloat(itemMatch.price) : itemMatch.price;

    setCartItems((prev) => {
      const working = [...prev];
      working[rowIndex] = {
        ...working[rowIndex],
        business_item_id: itemIdStr,
        title: itemMatch.title,
        unit_price: calculatedPrice || 0,
        total: (calculatedPrice || 0) * working[rowIndex].quantity,
      };
      return working;
    });
  };

  const handleQuantityValueModifier = (rowIndex: number, targetQtyStr: string): void => {
    const numericQty = parseInt(targetQtyStr, 10) || 1;
    setCartItems((prev) => {
      const working = [...prev];
      const targetRow = working[rowIndex];
      working[rowIndex] = {
        ...targetRow,
        quantity: numericQty,
        total: targetRow.unit_price * numericQty,
      };
      return working;
    });
  };

  // Mathematical Reduction Metrics
  const getSubtotalAmount = (): number => {
    return cartItems.reduce((acc, current) => acc + (current.total || 0), 0);
  };

  const getGrandTotalAmount = (): number => {
    const totalResult = getSubtotalAmount() - couponDiscount;
    return totalResult > 0 ? totalResult : 0;
  };

  const handleFormOrderSubmission = async (e: React.FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();

    if (!business?.id) {
      showToast({ icon: "error", title: "Context Conflict", text: "No operating context node identified." });
      return;
    }

    if (!customerName || !mobileNo) {
      showToast({ icon: "error", title: "Validation Warning", text: "Customer profiling criteria are mandatory." });
      return;
    }

    // Verify grid allocation content rules
    const structuredActiveItems = cartItems.filter((item) => item.business_item_id !== "");
    if (structuredActiveItems.length === 0) {
      showToast({ icon: "error", title: "Invoicing Voided", text: "Please specify at least one completely mapped ledger item sequence." });
      return;
    }

    if (locationType === "Home" && !deliveryAddress) {
      showToast({ icon: "error", title: "Logistics Requirements", text: "Home environment deliveries must state target locations." });
      return;
    }

    try {
      setSubmitting(true);

      const orderPayloadData = {
        business_id: business.id,
        customer: {
          name: customerName,
          mobile_no: mobileNo,
          email: email || null,
        },
        statuses: {
          order_status: orderStatus,
          payment_status: paymentStatus,
          payment_method: paymentMethod,
        },
        logistics: {
          location_type: locationType,
          address: locationType === "Center" ? "Served directly at corporate center campus setup." : deliveryAddress,
        },
        pricing: {
          subtotal: getSubtotalAmount(),
          coupon_code: null,
          coupon_amount: couponDiscount,
          grand_total: getGrandTotalAmount(),
        },
        notes: notes || null,
        order_items: structuredActiveItems.map((item) => ({
          business_item_id: Number(item.business_item_id),
          quantity: item.quantity,
          unit_price: item.unit_price,
          total: item.total,
        })),
      };

      const serverResponse = await _post(apiUrls.createOrder || "/api/orders/create", orderPayloadData);
      const outputStatus = serverResponse?.data?.success ?? serverResponse?.data?.success ?? false;

      if (outputStatus) {
        showToast({ icon: "success", title: "Invoice Created", text: "Order record written to database blocks successfully." });
        navigate("/orders");
      } else {
        showToast({ icon: "error", title: "Execution Stopped", text: serverResponse?.data?.message || "Remote pipeline failed compilation." });
      }
    } catch (err: any) {
      console.error(err);
      showToast({ icon: "error", title: "Database Fault", text: err?.response?.data?.message || "Failed to commit structure logs." });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="container-fluid p-0">
      <h1 className="h3 mb-3">
        Create Instant Order
        <Button className="float-end" variant="secondary" size="sm" onClick={() => navigate("/orders")}>
          Back to Ledger
        </Button>
      </h1>

      <Form onSubmit={handleFormOrderSubmission}>
        <Row className="g-4">
          
          {/* Main Work Space Left Parameter Panel Input Columns */}
          <Col lg={12}>
            <div className="card shadow-sm border-0 mb-4">
              <div className="card-body p-4">
                <div className="mb-4">
                  <h5 className="fw-bold text-dark text-uppercase small tracking-wide border-bottom pb-2 mb-3">
                    1. Customer Identification Profile
                  </h5>
                  <Row className="g-3">
                    <Col md={4}>
                      <Form.Group>
                        <Form.Label className="text-muted small fw-semibold">Client Full Name *</Form.Label>
                        <Form.Control type="text" placeholder="e.g. Asif Rahman" value={customerName} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setCustomerName(e.target.value)} required className="shadow-none" />
                      </Form.Group>
                    </Col>
                    <Col md={4}>
                      <Form.Group>
                        <Form.Label className="text-muted small fw-semibold">Contact Mobile Number *</Form.Label>
                        <Form.Control type="tel" placeholder="e.g. 01712345678" value={mobileNo} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setMobileNo(e.target.value)} required className="shadow-none" />
                      </Form.Group>
                    </Col>
                    <Col md={4}>
                      <Form.Group>
                        <Form.Label className="text-muted small fw-semibold">Email Address (Optional)</Form.Label>
                        <Form.Control type="email" placeholder="e.g. customer@link.com" value={email} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setEmail(e.target.value)} className="shadow-none" />
                      </Form.Group>
                    </Col>
                  </Row>
                </div>

                <div className="mb-4">
                  <h5 className="fw-bold text-dark text-uppercase small tracking-wide border-bottom pb-2 mb-3">
                    2. Transaction Metrics & Logistics Target
                  </h5>
                  <Row className="g-3 mb-3">
                    <Col md={4}>
                      <Form.Group>
                        <Form.Label className="text-muted small fw-semibold">Order Execution Status</Form.Label>
                        <Form.Select value={orderStatus} onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setOrderStatus(parseInt(e.target.value, 10))} className="form-select text-dark fw-medium shadow-none">
                          <option value={0}>⏳ Pending Approval</option>
                          <option value={1}>📦 Confirmed Processing</option>
                          <option value={2}>✅ Completed Lifecycle</option>
                          <option value={3}>❌ Cancelled Void</option>
                        </Form.Select>
                      </Form.Group>
                    </Col>
                    <Col md={4}>
                      <Form.Group>
                        <Form.Label className="text-muted small fw-semibold">Payment Processing State</Form.Label>
                        <Form.Select value={paymentStatus} onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setPaymentStatus(parseInt(e.target.value, 10))} className="form-select text-dark fw-medium shadow-none">
                          <option value={0}>🔴 Unpaid Balance</option>
                          <option value={1}>🟢 Fully Paid Settled</option>
                        </Form.Select>
                      </Form.Group>
                    </Col>
                    <Col md={4}>
                      <Form.Group>
                        <Form.Label className="text-muted small fw-semibold">Payment Ledger Method</Form.Label>
                        <Form.Select value={paymentMethod} onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setPaymentMethod(e.target.value)} className="form-select text-dark fw-medium shadow-none">
                          <option value="Cash">Cash Handover</option>
                          <option value="COD">Cash On Delivery (COD)</option>
                          <option value="Digital">Digital Wallet (bKash/Nagad)</option>
                          <option value="Card">Visa/Mastercard Terminal</option>
                        </Form.Select>
                      </Form.Group>
                    </Col>
                  </Row>

                  <Row className="g-3">
                    <Col md={12}>
                      <Form.Group>
                        <Form.Label className="text-muted small d-block fw-semibold">Logistics Fulfillment Context</Form.Label>
                        <div className="d-flex gap-4 mt-1">
                          <Form.Check type="radio" label="🏢 Center Walk-In" name="order_loc_type" id="ord_center" checked={locationType === "Center"} onChange={() => setLocationType("Center")} className="small fw-medium" />
                          <Form.Check type="radio" label="🏠 Home Delivery Dispatch" name="order_loc_type" id="ord_home" checked={locationType === "Home"} onChange={() => setLocationType("Home")} className="small text-primary fw-medium" />
                        </div>
                      </Form.Group>
                    </Col>

                    {locationType === "Home" && (
                      <Col md={12}>
                        <Form.Group>
                          <Form.Label className="text-muted small fw-semibold text-primary">Explicit Shipping Address Destination *</Form.Label>
                          <Form.Control as="textarea" rows={2} placeholder="Type exact block street numbers, flats or operational route parameters..." value={deliveryAddress} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setDeliveryAddress(e.target.value)} required className="shadow-none" />
                        </Form.Group>
                      </Col>
                    )}
                  </Row>
                </div>
              </div>
            </div>
          </Col>

          {/* Dynamic Cascading Tabular Processing Panel */}
          <Col lg={12}>
            <div className="card shadow-sm border-0 mb-4">
              <div className="card-body p-4">
                <div className="d-flex justify-content-between align-items-center border-bottom pb-2 mb-3">
                  <h5 className="fw-bold text-dark text-uppercase small tracking-wide m-0">
                    3. Dynamic Invoice Item Matrix Breakdown
                  </h5>
                  <Button type="button" variant="primary" size="sm" onClick={handleAddNewRowInstance} disabled={metaLoading} className="fw-semibold px-3 shadow-none">
                    ➕ Append Line Item Row
                  </Button>
                </div>

                <div className="table-responsive">
                  <Table bordered className="align-middle bg-white" style={{ minWidth: "900px" }}>
                    <thead className="table-light text-muted small uppercase">
                      <tr>
                        <th style={{ width: "15%" }}>Group Priority</th>
                        <th style={{ width: "20%" }}>Category Segment</th>
                        <th style={{ width: "20%" }}>Subcategory Node</th>
                        <th style={{ width: "20%" }}>Target Service Item Asset</th>
                        <th style={{ width: "10%" }} className="text-center">Quantity</th>
                        <th style={{ width: "10%" }} className="text-end">Row Aggregate</th>
                        <th style={{ width: "5%" }} className="text-center">Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {cartItems.length > 0 ? (
                        cartItems.map((item, index) => (
                          <tr key={item.id}>
                            {/* Group Tier */}
                            <td>
                              <Form.Select size="sm" value={item.group} onChange={(e) => handleGroupSelectionChange(index, e.target.value)} className="shadow-none" required>
                                <option value="" disabled>-- Group --</option>
                                {availableGroups.map((group) => (
                                  <option key={group} value={group}>{group.toUpperCase()}</option>
                                ))}
                              </Form.Select>
                            </td>

                            {/* Category Tier */}
                            <td>
                              <Form.Select size="sm" value={item.categoryId} onChange={(e) => handleCategorySelectionChange(index, e.target.value)} disabled={!item.group} className="shadow-none" required>
                                <option value="" disabled>-- Category --</option>
                                {item.availableCategories.map((catNode) => (
                                  <option key={catNode.category?.id} value={catNode.category?.id}>
                                    {catNode.category?.name_en}
                                  </option>
                                ))}
                              </Form.Select>
                            </td>

                            {/* Subcategory Tier */}
                            <td>
                              <Form.Select size="sm" value={item.subcategoryId} onChange={(e) => handleSubcategorySelectionChange(index, e.target.value)} disabled={!item.categoryId} className="shadow-none" required>
                                <option value="" disabled>-- Subcategory --</option>
                                {item.availableSubcategories.map((subNode) => (
                                  <option key={subNode.subcategory?.id} value={subNode.subcategory?.id}>
                                    {subNode.subcategory?.name_en}
                                  </option>
                                ))}
                              </Form.Select>
                            </td>

                            {/* Downstream Item Asset Selection */}
                            <td>
                              <div className="position-relative">
                                <Form.Select size="sm" value={item.business_item_id} onChange={(e) => handleItemSelectionChange(index, e.target.value)} disabled={!item.subcategoryId || item.itemsLoading} className="shadow-none" required>
                                  <option value="" disabled>{item.itemsLoading ? "Loading items..." : "-- Choose Asset Item --"}</option>
                                  {item.availableItems.map((srv) => (
                                    <option key={srv.id} value={srv.id}>
                                      {srv.title} ({Number(srv.price).toFixed(2)} BDT)
                                    </option>
                                  ))}
                                </Form.Select>
                                {item.itemsLoading && (
                                  <div className="position-absolute end-0 top-50 translate-middle-y pe-4 me-2">
                                    <Spinner animation="border" size="sm" variant="secondary" style={{ width: "0.85rem", height: "0.85rem" }} />
                                  </div>
                                )}
                              </div>
                            </td>

                            {/* Quantity Allocation Fields */}
                            <td>
                              <Form.Control type="number" size="sm" min="1" value={item.quantity} onChange={(e) => handleQuantityValueModifier(index, e.target.value)} disabled={!item.business_item_id} className="text-center shadow-none" required />
                            </td>

                            {/* Total Line Calculations */}
                            <td className="text-end font-monospace fw-bold text-dark">
                              {item.total.toFixed(2)} BDT
                            </td>

                            {/* Delete Options Button Row */}
                            <td className="text-center">
                              <Button type="button" variant="outline-danger" size="sm" onClick={() => handleRemoveRowInstance(item.id)} className="p-1 border-0 shadow-none line-item-delete">
                                ❌
                              </Button>
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan={7} className="text-center py-4 text-muted fw-medium small">
                            No functional line configurations declared inside this sheet setup yet. Click "Append Line Item Row" above to write records.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </Table>
                </div>
              </div>
            </div>
          </Col>

          {/* Mathematical Valuation Calculation Ledger Stack */}
          <Col lg={12}>
            <div className="card shadow-sm border-0 mb-4">
              <div className="card-body p-4">
                <Row className="align-items-center g-3">
                  <Col md={4}>
                    <Form.Group>
                      <Form.Label className="text-muted small fw-semibold">Internal Administrative Operations Notes</Form.Label>
                      <Form.Control as="textarea" rows={2} placeholder="Optional billing descriptions or processing comments..." value={notes} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setNotes(e.target.value)} className="shadow-none small" />
                    </Form.Group>
                  </Col>

                  <Col md={4}>
                    <Form.Group>
                      <Form.Label className="text-muted small fw-semibold">Manual Discount Allocation Markdown (BDT)</Form.Label>
                      <InputGroup size="sm">
                        <InputGroup.Text className="text-muted font-monospace bg-light">BDT</InputGroup.Text>
                        <Form.Control type="number" min="0" placeholder="0.00" value={couponDiscount || ""} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setCouponDiscount(Math.abs(parseFloat(e.target.value) || 0))} className="shadow-none fw-medium text-danger" />
                      </InputGroup>
                    </Form.Group>
                  </Col>

                  <Col md={4} className="bg-light rounded p-3 text-end ms-auto" style={{ maxWidth: "380px" }}>
                    <div className="d-flex justify-content-between mb-1 text-muted small">
                      <span>Gross Cartesian Subtotal:</span>
                      <span className="fw-semibold text-dark font-monospace">{getSubtotalAmount().toFixed(2)} BDT</span>
                    </div>
                    {couponDiscount > 0 && (
                      <div className="d-flex justify-content-between mb-1 text-danger small fw-medium">
                        <span>Discount Deduction:</span>
                        <span className="font-monospace">- {couponDiscount.toFixed(2)} BDT</span>
                      </div>
                    )}
                    <div className="d-flex justify-content-between align-items-center border-top pt-2 mt-2 fw-bold text-dark">
                      <span className="small text-uppercase tracking-wider">Net Amount Payable:</span>
                      <span className="text-primary fs-4 font-monospace">{getGrandTotalAmount().toFixed(2)} BDT</span>
                    </div>
                  </Col>
                </Row>

                <div className="d-flex justify-content-end gap-2 border-top pt-3 mt-4">
                  <Button type="button" variant="outline-secondary" size="sm" disabled={submitting} onClick={() => navigate("/orders")} className="px-4">
                    Discard Invoice Form
                  </Button>
                  <Button type="submit" variant="primary" size="sm" disabled={submitting || metaLoading} className="fw-bold px-4">
                    {submitting ? <Spinner animation="border" size="sm" /> : "Complete & Dispatch Order"}
                  </Button>
                </div>
              </div>
            </div>
          </Col>

        </Row>
      </Form>
    </div>
  );
}